import { Graphics, LINE_CAP, LINE_JOIN } from "pixi.js"

// The look the building controls share: a builder's plan board.
//
// Both of them -- the desktop shape palette that opens under the cursor on Shift, and the mobile
// build dock -- used to be flat slate cards with rounded icon buttons. That is the shape of a
// desktop toolbar, and over pixel grass it read as browser chrome sitting on top of the game rather
// than a part of it. They are drawn here instead as cut timber with a bronze rim and corner rivets,
// each tool standing on its own plot of squared paper, so the footprint being chosen reads in the
// same tiles the build cursor snaps to.
//
// It is one module for the same reason dial.ts is: the two widgets are the same object on two input
// devices, and nothing would stop their frames drifting apart if each drew its own.
//
// RIM, GOLD and INK are building_print_countdown.ts's own colours. That dial appears over the walls
// these controls queue, within a second of them, and the three must not look like they come from
// different games.
export const TIMBER = 0x3d2e20
export const TIMBER_EDGE = 0x120d0a
export const FACE = 0x15120d
export const RIM = 0x8e7b58
export const RIM_LIGHT = 0xd9b483
export const GOLD = 0xffd166
export const INK = 0xffedc7
export const MUTED = 0xa08c6c
export const PLOT_FACE = 0x0f120c
export const PLOT_FACE_ON = 0x241b10
export const PLOT_RIM = 0x4a3f30
export const GRID_COLOR = 0x7d8a63
export const GLYPH = 0xc3cbb4

// Cut corners rather than rounded ones: a radius is what every dialog box on a desktop has; a
// chamfer is what a sawn board has.
export const BOARD_CHAMFER = 7
export const BOARD_FRAME = 4
const PLOT_CHAMFER = 4
// Squared paper, and the unit the footprints are measured in.
export const GRID_PITCH = 8

/** One chamfered outline, issued as a path so a fill and a stroke describe the same edge. */
export const chamfer = (g: Graphics, x: number, y: number, w: number, h: number, c: number) => {
    g.moveTo(x + c, y)
        .lineTo(x + w - c, y).lineTo(x + w, y + c)
        .lineTo(x + w, y + h - c).lineTo(x + w - c, y + h)
        .lineTo(x + c, y + h).lineTo(x, y + h - c)
        .lineTo(x, y + c).closePath()
}

/**
 * A bolt through the board. Nothing structural -- rivets are what stop a frame reading as a CSS
 * border, and they cost three circles each, drawn once.
 */
export const rivet = (g: Graphics, x: number, y: number) => {
    g.lineStyle(0)
    g.beginFill(TIMBER_EDGE, 0.95).drawCircle(x, y, 2.4).endFill()
    g.beginFill(RIM, 0.95).drawCircle(x, y, 1.6).endFill()
    g.beginFill(RIM_LIGHT, 0.7).drawCircle(x - 0.5, y - 0.55, 0.7).endFill()
}

/** The board itself: dropped silhouette, timber band, lit top edge, inner face and four rivets. */
export const drawBoard = (g: Graphics, w: number, h: number) => {
    // The board lands over grass and wall ghosts and has to read as an object sitting on them
    // rather than a decal painted into them. (The print dial drops the same silhouette.)
    g.beginFill(0x000000, 0.32)
    chamfer(g, 0, 4, w, h, BOARD_CHAMFER)
    g.endFill()
    g.lineStyle({ width: 1.5, color: TIMBER_EDGE, alpha: 0.95, join: LINE_JOIN.MITER })
    g.beginFill(TIMBER, 0.98)
    chamfer(g, 0, 0, w, h, BOARD_CHAMFER)
    g.endFill()
    g.lineStyle({ width: 1, color: RIM, alpha: 0.85, join: LINE_JOIN.MITER })
    g.beginFill(FACE, 0.97)
    chamfer(g, BOARD_FRAME, BOARD_FRAME, w - BOARD_FRAME * 2, h - BOARD_FRAME * 2, BOARD_CHAMFER - 2)
    g.endFill()
    // One lit edge along the top, so the board has a direction the light falls from.
    g.lineStyle(1, RIM_LIGHT, 0.16)
        .moveTo(BOARD_FRAME + BOARD_CHAMFER, BOARD_FRAME + 1.5)
        .lineTo(w - BOARD_FRAME - BOARD_CHAMFER, BOARD_FRAME + 1.5)
    for (const [x, y] of [[10, 10], [w - 10, 10], [10, h - 10], [w - 10, h - 10]]) rivet(g, x, y)
}

/** An engraved groove, for the footnote line a board carries under its tools. */
export const groove = (g: Graphics, x0: number, x1: number, y: number) => {
    g.lineStyle(1, 0x000000, 0.6).moveTo(x0, y).lineTo(x1, y)
    g.lineStyle(1, RIM_LIGHT, 0.16).moveTo(x0, y + 1).lineTo(x1, y + 1)
}

/** The plate one tool stands on: dug ground, with the chosen one lit gold. */
export const drawPlot = (g: Graphics, w: number, h: number, selected: boolean, hovered: boolean) => {
    const rimColor = selected ? GOLD : hovered ? RIM : PLOT_RIM
    // A fatter, faint copy behind the chosen plot: a glow without a filter, which this hardware
    // target cannot spare (the same trick as the print dial's arc).
    if (selected) {
        g.lineStyle({ width: 3, color: GOLD, alpha: 0.13, join: LINE_JOIN.MITER })
        chamfer(g, 0.5, 0.5, w - 1, h - 1, PLOT_CHAMFER)
    }
    g.lineStyle({ width: 1, color: rimColor, alpha: selected ? 1 : 0.8, join: LINE_JOIN.MITER })
    g.beginFill(selected ? PLOT_FACE_ON : PLOT_FACE, selected ? 0.95 : 0.8)
    chamfer(g, 0.5, 0.5, w - 1, h - 1, PLOT_CHAMFER)
    g.endFill()
}

/**
 * Squared paper over a plot, laid out around the point the footprint is centred on so the two
 * always agree: verticals fall on the footprint's own edges, horizontals on its centre line.
 */
export const plotGrid = (g: Graphics, box: { x: number; y: number; w: number; h: number },
    cx: number, cy: number, lit: boolean) => {
    g.lineStyle(1, GRID_COLOR, lit ? 0.3 : 0.17)
    for (let x = cx + GRID_PITCH / 2; x < box.x + box.w; x += GRID_PITCH) g.moveTo(x, box.y).lineTo(x, box.y + box.h)
    for (let x = cx - GRID_PITCH / 2; x > box.x; x -= GRID_PITCH) g.moveTo(x, box.y).lineTo(x, box.y + box.h)
    for (let y = cy; y < box.y + box.h; y += GRID_PITCH) g.moveTo(box.x, y).lineTo(box.x + box.w, y)
    for (let y = cy - GRID_PITCH; y > box.y; y -= GRID_PITCH) g.moveTo(box.x, y).lineTo(box.x + box.w, y)
}

// What a tool will lay down. "single" is the one-tile tap the mobile dock starts in; the rest are
// the drawn shapes both devices offer.
export type Footprint = "rectangle" | "ellipse" | "line" | "single"

/** The footprint path, three grid cells by two, centred on the point the grid was laid around. */
export const footprint = (g: Graphics, kind: Footprint, cx: number, cy: number) => {
    const hw = GRID_PITCH * 1.5
    const hh = GRID_PITCH
    if (kind === "rectangle") g.drawRect(cx - hw, cy - hh, hw * 2, hh * 2)
    else if (kind === "ellipse") g.drawEllipse(cx, cy, hw, hh)
    else if (kind === "single") g.drawRect(cx - hh / 2, cy - hh / 2, hh, hh)
    else g.moveTo(cx - hw, cy + hh).lineTo(cx + hw, cy - hh)
}

/** Square caps and mitred joins: a chunky stroke on the grid, not an icon-font hairline. */
export const footprintStroke = (g: Graphics, color: number) =>
    g.lineStyle({ width: 2, color, alpha: 1, cap: LINE_CAP.SQUARE, join: LINE_JOIN.MITER })
